'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

export interface WindowGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Defaults {
  position: { x: number; y: number } | 'top-right' | 'bottom-right';
  width: number;
  height: number;
}

const EDGE = 24;
const HEADER_STRIP = 40; // px of title bar that must stay reachable on screen

/** Highest z-index handed out so far. Clicking a window raises it above its sibling. */
let topLayer = 40;
const MAX_LAYER = 48; // ImageModal sits at z-50 and must never be covered

function resolveDefaults(defaults: Defaults): WindowGeometry {
  const vw = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const vh = typeof window === 'undefined' ? 800 : window.innerHeight;
  const { position, width, height } = defaults;
  if (position === 'top-right') return { x: vw - width - EDGE, y: 80, width, height };
  if (position === 'bottom-right') return { x: vw - width - EDGE, y: vh - height - EDGE, width, height };
  return { x: position.x, y: position.y, width, height };
}

/**
 * Pull a window back into view.
 *
 * Bring it *fully* on screen: merely keeping a sliver of header grabbable leaves the
 * controls at the header's right edge (close, clear, model picker) off-screen and
 * unclickable, and a body hanging below the fold hides the composer and resize handle.
 *
 * Size is fitted before position, because a window larger than the viewport has no good
 * position — every choice strands either the title or the controls at the far end. Its
 * content scales to the frame either way, so panning such a window would reveal nothing.
 */
function clamp(geo: WindowGeometry): WindowGeometry {
  if (typeof window === 'undefined') return geo;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.max(200, Math.min(geo.width, vw - EDGE * 2));
  const height = Math.max(HEADER_STRIP, Math.min(geo.height, vh - EDGE * 2));
  return {
    width,
    height,
    x: Math.min(Math.max(geo.x, EDGE), vw - width - EDGE),
    y: Math.min(Math.max(geo.y, EDGE), vh - height - EDGE),
  };
}

/**
 * Position/size for one floating window, persisted per key.
 *
 * Restores clamped: geometry saved on a wider monitor (or dragged off-screen) would
 * otherwise come back unreachable.
 */
function useWindowGeometry(storageKey: string, defaults: Defaults) {
  const [geo, setGeo] = useState<WindowGeometry | null>(null);

  useEffect(() => {
    let restored: WindowGeometry | null = null;
    try {
      const raw = localStorage.getItem(`floating-window:${storageKey}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed?.x === 'number' && typeof parsed?.width === 'number') restored = parsed;
      }
    } catch {
      /* fall back to defaults */
    }
    setGeo(clamp(restored ?? resolveDefaults(defaults)));
    // defaults is a fresh object each render; keying on storageKey is the intent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const persist = useCallback((next: WindowGeometry) => {
    setGeo(next);
    try {
      localStorage.setItem(`floating-window:${storageKey}`, JSON.stringify(next));
    } catch {
      /* non-persistent is fine */
    }
  }, [storageKey]);

  // A viewport that shrinks (or a restored monitor change) must not strand the window
  useEffect(() => {
    const onResize = () => setGeo((g) => (g ? clamp(g) : g));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return { geo, setGeo, persist };
}

interface FloatingWindowProps {
  title: string;
  open: boolean;
  onClose: () => void;
  /** Distinguishes persisted geometry between windows. */
  storageKey: string;
  defaultPosition?: Defaults['position'];
  defaultWidth?: number;
  defaultHeight?: number;
  minWidth?: number;
  maxWidth?: number;
  /** 'x' resizes width only (the preview clamps to its image); 'both' for panels. */
  resizeAxis?: 'x' | 'both';
  /**
   * Natural pixel size of the content. Supplying it puts zoom badges in the header that
   * snap the window to a multiple of that size, so hitting 1:1 doesn't mean nudging a
   * corner by hand. On an 'x' window only the width is set, matching the drag handle.
   */
  contentSize?: { width: number; height: number };
  /** Multipliers offered as header badges. Fractions are fine — 0.5 halves the content. */
  zoomPresets?: number[];
  /**
   * Tie the frame to `contentSize`'s aspect ratio: the handle scales both axes together,
   * and the frame re-fits when the content's own ratio changes. Without it a window that
   * does not match its image either crops it or pads it with dead space.
   */
  lockAspect?: boolean;
  headerRight?: ReactNode;
  children: ReactNode;
}

/**
 * A draggable, resizable panel that floats over the page.
 *
 * Dragging is bound to the header alone — the body holds scrollable content and inputs
 * that must keep normal pointer behaviour.
 */
export default function FloatingWindow({
  title,
  open,
  onClose,
  storageKey,
  defaultPosition = 'top-right',
  defaultWidth = 420,
  defaultHeight = 520,
  minWidth = 280,
  maxWidth,
  resizeAxis = 'both',
  contentSize,
  zoomPresets = [0.5, 1],
  lockAspect = false,
  headerRight,
  children,
}: FloatingWindowProps) {
  const { geo, setGeo, persist } = useWindowGeometry(storageKey, {
    position: defaultPosition,
    width: defaultWidth,
    height: defaultHeight,
  });

  const [layer, setLayer] = useState(41);
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const resizeStart = useRef({ mouseX: 0, mouseY: 0, width: 0, height: 0 });
  const headerRef = useRef<HTMLDivElement>(null);

  // The gesture handlers are bound once per gesture, so they cannot close over `geo` and
  // still see the last mousemove. A ref tracks it for the mouseup that has to clamp.
  const geoRef = useRef<WindowGeometry | null>(null);
  geoRef.current = geo;

  const aspect = lockAspect && contentSize ? contentSize.width / contentSize.height : null;

  /** Frame height that shows `width` of content at `aspect`, header included. */
  const heightFor = useCallback((width: number, ratio: number) =>
    Math.round(width / ratio) + (headerRef.current?.offsetHeight ?? HEADER_STRIP), []);

  /** Snap the *content* to `factor`× its natural size, shrinking to fit the viewport. */
  const applyZoom = useCallback((factor: number) => {
    const g = geoRef.current;
    if (!g || !contentSize) return;
    const maxW = Math.min(window.innerWidth - EDGE * 2, maxWidth ?? Infinity);
    if (resizeAxis === 'x') {
      // Width only, exactly what the drag handle does on this axis. The body derives its
      // own height from the image's aspect ratio, so writing a height here would change
      // the window's proportions in a way dragging never does.
      persist(clamp({ ...g, width: Math.round(Math.max(minWidth, Math.min(contentSize.width * factor, maxW))) }));
      return;
    }
    const chrome = headerRef.current?.offsetHeight ?? HEADER_STRIP;
    const scale = Math.min(
      factor,
      maxW / contentSize.width,
      (window.innerHeight - EDGE * 2 - chrome) / contentSize.height,
    );
    persist(clamp({
      ...g,
      width: Math.round(Math.max(minWidth, contentSize.width * scale)),
      height: Math.round(contentSize.height * scale) + chrome,
    }));
  }, [contentSize, persist, resizeAxis, minWidth, maxWidth]);

  // Re-fit when the content's ratio changes — switching the page to 9:16 leaves a frame
  // shaped for the old ratio, which is exactly the cropping the lock exists to prevent.
  // Width is kept, so the user's chosen size survives; only the height follows.
  const fittedFor = useRef<number | null>(null);
  useEffect(() => {
    const g = geoRef.current;
    // headerRef is the gate, not `open`: a closed window renders nothing, so its header
    // has no height yet and fitting against the fallback lands a pixel out.
    if (!aspect || !g || !headerRef.current || fittedFor.current === aspect) return;
    fittedFor.current = aspect;
    const chrome = headerRef.current.offsetHeight;
    const width = Math.min(g.width, (window.innerHeight - EDGE * 2 - chrome) * aspect);
    persist(clamp({ ...g, width: Math.round(width), height: heightFor(width, aspect) }));
  }, [aspect, geo, open, heightFor, persist]);

  const raise = useCallback(() => {
    topLayer = Math.min(topLayer + 1, MAX_LAYER);
    setLayer(topLayer);
  }, []);

  useEffect(() => {
    if (open) raise();
  }, [open, raise]);

  useEffect(() => {
    if (!dragging) return;
    const up = () => {
      setDragging(false);
      if (geoRef.current) persist(clamp(geoRef.current));
    };
    const move = (e: MouseEvent) => {
      // The button came back up while we weren't looking — released over the OS chrome or
      // another window, where the document never sees the mouseup. Settle here instead,
      // otherwise the window stays glued to the cursor and never snaps back.
      if (e.buttons === 0) return up();
      setGeo((g) => (g ? { ...g, x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y } : g));
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    window.addEventListener('blur', up);
    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      window.removeEventListener('blur', up);
    };
  }, [dragging, setGeo, persist]);

  useEffect(() => {
    if (!resizing) return;
    const up = () => {
      setResizing(false);
      // Clamp, not just persist: growing a window pushes its right edge past the viewport
      // while x stays put, which strands the header controls off-screen.
      if (geoRef.current) persist(clamp(geoRef.current));
    };
    const move = (e: MouseEvent) => {
      if (e.buttons === 0) return up();
      setGeo((g) => {
        if (!g) return g;
        const dx = e.clientX - resizeStart.current.mouseX;
        const dy = e.clientY - resizeStart.current.mouseY;
        // Capped to the viewport as well as to maxWidth: a window bigger than the screen
        // puts its own resize handle out of reach.
        const maxW = Math.min(maxWidth ?? Infinity, window.innerWidth - EDGE * 2);
        const maxH = window.innerHeight - EDGE * 2;
        if (aspect) {
          const chrome = headerRef.current?.offsetHeight ?? HEADER_STRIP;
          // A corner drag has two axes but only one degree of freedom once the ratio is
          // fixed. Follow whichever axis moved further, by magnitude rather than value —
          // taking the larger value would let a stationary dx veto a drag straight up, so
          // the window could grow vertically but never shrink.
          const byY = dy * aspect;
          const lead = Math.abs(dx) >= Math.abs(byY) ? dx : byY;
          const width = Math.max(minWidth, Math.min(
            resizeStart.current.width + lead, maxW, (maxH - chrome) * aspect));
          return { ...g, width: Math.round(width), height: heightFor(width, aspect) };
        }
        const width = Math.max(minWidth, Math.min(resizeStart.current.width + dx, maxW));
        const height = resizeAxis === 'both'
          ? Math.max(200, Math.min(resizeStart.current.height + dy, maxH))
          : g.height;
        return { ...g, width, height };
      });
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    window.addEventListener('blur', up);
    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      window.removeEventListener('blur', up);
    };
  }, [resizing, minWidth, maxWidth, resizeAxis, aspect, heightFor, setGeo, persist]);

  if (!open || !geo) return null;

  return (
    <div
      className="fixed rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] shadow-2xl flex flex-col overflow-hidden"
      style={{ left: geo.x, top: geo.y, width: geo.width, height: geo.height, zIndex: layer }}
      onMouseDown={raise}
      data-floating-window={storageKey}
    >
      <div
        ref={headerRef}
        className="flex items-center gap-2 px-3 py-2 border-b border-[var(--border-subtle)] bg-[var(--bg-elevated)] cursor-move select-none shrink-0"
        onMouseDown={(e) => {
          if ((e.target as HTMLElement).closest('button, select, input')) return;
          dragOffset.current = { x: e.clientX - geo.x, y: e.clientY - geo.y };
          setDragging(true);
        }}
      >
        <span className="text-sm font-medium truncate">{title}</span>
        {contentSize && (
          <div className="flex items-center gap-1 shrink-0">
            {zoomPresets.map((f) => {
              const active = Math.abs(geo.width - contentSize.width * f) <= 2;
              return (
                <button
                  key={f}
                  data-zoom-preset={f}
                  onClick={() => applyZoom(f)}
                  className={`px-1.5 py-0.5 rounded text-[11px] leading-none font-medium tabular-nums transition-colors cursor-pointer ${
                    active
                      ? 'bg-[var(--accent)] text-black'
                      : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
                  }`}
                  title={`Resize to ${Math.round(contentSize.width * f)}×${Math.round(contentSize.height * f)}`}
                >
                  ×{f}
                </button>
              );
            })}
          </div>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          {headerRight}
          <button
            onClick={onClose}
            className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] cursor-pointer"
            title="Close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">{children}</div>

      <div
        onMouseDown={(e) => {
          e.preventDefault();
          resizeStart.current = { mouseX: e.clientX, mouseY: e.clientY, width: geo.width, height: geo.height };
          setResizing(true);
        }}
        className={`absolute bottom-0 right-0 w-4 h-4 ${resizeAxis === 'both' ? 'cursor-nwse-resize' : 'cursor-ew-resize'}`}
        title="Resize"
        data-resize-handle
      >
        <svg className="w-3 h-3 absolute bottom-0.5 right-0.5 text-[var(--text-muted)] opacity-60" viewBox="0 0 12 12">
          <path d="M11 5 5 11M11 9l-2 2" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  );
}
